/**
 * cam-toolpath-utils — 2D CAM toolpath için saf (yan etkisiz) geometri yardımcıları.
 *
 * Aktivitelerden (Cep/Profil) çıkarıldı → test edilebilir + DRY.
 * Tümü deterministik, THREE'ye bağımsız (sadece {x,y} kullanır).
 */

export interface IXY { x: number; y: number; }

export interface ITabParams {
    tabSayisi: number;
    tabDagitim: 'Sayi' | 'Mesafe';
    tabMesafe: number;
    tabGenislik: number;
    tabYukseklik: number;
    tabSekli: 'Dikdortgen' | 'Ucgen';
}

/** Kapalı poligonun çevresi ve segment uzunlukları */
export function poligonCevresi(pts: IXY[]): { cevre: number; segLen: number[] } {
    const n = pts.length;
    const segLen: number[] = [];
    let cevre = 0;
    for (let i = 0; i < n; i++) {
        const a = pts[i], b = pts[(i + 1) % n];
        const d = Math.hypot(b.x - a.x, b.y - a.y);
        segLen.push(d); cevre += d;
    }
    return { cevre, segLen };
}

/** Tab merkezlerinin arc-length konumları (çevre boyunca eşit dağıtılmış) */
export function tabMerkezleri(cevre: number, p: ITabParams): number[] {
    let tabSayisi = p.tabSayisi;
    if (p.tabDagitim === 'Mesafe' && p.tabMesafe > 0) {
        tabSayisi = Math.max(1, Math.round(cevre / p.tabMesafe));
    }
    tabSayisi = Math.max(0, Math.min(tabSayisi, 64));
    const merkezler: number[] = [];
    for (let t = 0; t < tabSayisi; t++) merkezler.push((t + 0.5) / tabSayisi * cevre);
    return merkezler;
}

/**
 * Arc-length s konumundaki Z. Tab dışında altZ; içinde:
 * Dikdörtgen → düz tepe (altZ+tabYukseklik), Üçgen → kenarda altZ, merkezde tepe (rampalı).
 */
export function tabZHesap(
    s: number, merkezler: number[], cevre: number,
    yariGen: number, altZ: number, tabYukseklik: number,
    sekil: 'Dikdortgen' | 'Ucgen'
): number {
    const h = Math.max(0, tabYukseklik);
    for (const m of merkezler) {
        let d = Math.abs(s - m);
        d = Math.min(d, cevre - d); // dairesel mesafe
        if (d <= yariGen) {
            if (sekil === 'Ucgen') {
                const oran = 1 - (d / yariGen); // kenar 0 → merkez 1
                return altZ + h * oran;
            }
            return altZ + h;
        }
    }
    return altZ;
}

/** Yay/trochoidal segmenti ~stepRad adımlarla noktalara böler (start hariç, end dahil) */
export function yayNoktalari(
    start: IXY, end: IXY, center: IXY, radius: number,
    clockwise: boolean, sweepAngle?: number, stepRad: number = Math.PI / 36
): IXY[] {
    const a0 = Math.atan2(start.y - center.y, start.x - center.x);
    const a1 = Math.atan2(end.y - center.y, end.x - center.x);
    let sweep: number;
    if (sweepAngle != null) {
        sweep = clockwise ? -Math.abs(sweepAngle) : Math.abs(sweepAngle);
    } else {
        sweep = a1 - a0;
        if (clockwise && sweep > 0) sweep -= Math.PI * 2;
        if (!clockwise && sweep < 0) sweep += Math.PI * 2;
    }
    const adimSayisi = Math.max(4, Math.ceil(Math.abs(sweep) / stepRad));
    const out: IXY[] = [];
    for (let i = 1; i <= adimSayisi; i++) {
        const a = a0 + sweep * (i / adimSayisi);
        out.push({ x: center.x + radius * Math.cos(a), y: center.y + radius * Math.sin(a) });
    }
    return out;
}

export interface IToolSegment {
    type: string;
    start: IXY;
    end: IXY;
    center?: IXY;
    radius?: number;
    clockwise?: boolean;
    sweepAngle?: number;
    feedrate?: number;
}

/** Feed çarpanını emniyetli aralığa kelepçeler (geçersiz → 1) */
export function feedCarpanNormalize(f: number | undefined): number {
    const v = (typeof f === 'number' && isFinite(f)) ? f : 1;
    return Math.max(0.2, Math.min(1.5, v));
}

/**
 * ToolpathSegment dizisini sürekli yollara (+ paralel feed çarpanları) böler.
 * Süreksizlikte (segment başı önceki sona bağlı değil) yeni yol başlar.
 * Arc/trochoidal segmentler tessela edilir. Her noktaya segmentin feed çarpanı atanır.
 */
export function segmentleriYollara(segmentler: IToolSegment[], eps = 1e-3): { yollar: IXY[][]; hizlar: number[][] } {
    const yollar: IXY[][] = [];
    const hizlar: number[][] = [];
    let aktif: IXY[] = [];
    let aktifHiz: number[] = [];

    const ekle = (x: number, y: number, hiz: number) => {
        const son = aktif[aktif.length - 1];
        if (son && Math.abs(son.x - x) < eps && Math.abs(son.y - y) < eps) return;
        aktif.push({ x, y }); aktifHiz.push(hiz);
    };
    const kapat = () => {
        if (aktif.length >= 2) { yollar.push(aktif); hizlar.push(aktifHiz); }
        aktif = []; aktifHiz = [];
    };

    for (const seg of segmentler) {
        const hiz = feedCarpanNormalize(seg.feedrate);
        if (aktif.length > 0) {
            const son = aktif[aktif.length - 1];
            if (Math.abs(son.x - seg.start.x) > eps || Math.abs(son.y - seg.start.y) > eps) kapat();
        }
        if (aktif.length === 0) ekle(seg.start.x, seg.start.y, hiz);

        if ((seg.type === 'arc' || seg.type === 'trochoidal') && seg.center && seg.radius) {
            const pts = yayNoktalari(seg.start, seg.end, seg.center, seg.radius, seg.clockwise === true, seg.sweepAngle);
            for (const p of pts) ekle(p.x, p.y, hiz);
        } else {
            ekle(seg.end.x, seg.end.y, hiz);
        }
    }
    kapat();
    return { yollar, hizlar };
}

export interface IToolpathNokta { x: number; y: number; z: number; tip?: string; hiz?: number; }
export interface IToolpathStat { toplamMesafe: number; kesmeMesafe: number; rapidMesafe: number; sureSn: number; hareketSayisi: number; }

/** Toolpath istatistiği: kesme/rapid mesafe (mm) + tahmini süre (sn). rapidHiz mm/min. */
export function toolpathIstatistik(noktalar: IToolpathNokta[], rapidHiz = 10000): IToolpathStat {
    let kesme = 0, rapid = 0, sureSn = 0;
    for (let i = 1; i < noktalar.length; i++) {
        const a = noktalar[i - 1], b = noktalar[i];
        const d = Math.hypot(b.x - a.x, b.y - a.y, b.z - a.z);
        if (d < 1e-9) continue;
        if (b.tip === 'rapid') {
            rapid += d; sureSn += d / (Math.max(1, rapidHiz) / 60);
        } else {
            kesme += d; const f = Math.max(1, b.hiz || 1000); sureSn += d / (f / 60);
        }
    }
    return { toplamMesafe: kesme + rapid, kesmeMesafe: kesme, rapidMesafe: rapid, sureSn, hareketSayisi: noktalar.length };
}

export interface IGkodYay { i: number; j: number; arcCW: boolean; }

/** Yay → G02/G03 I/J (incremental, başlangıçtan merkeze). arcCW true=G02 (saat yönü). */
export function yayGKodu(baslangic: IXY, merkez: IXY, cw: boolean): IGkodYay {
    return { i: merkez.x - baslangic.x, j: merkez.y - baslangic.y, arcCW: cw };
}

/**
 * G02/G03 semantiğiyle yay noktaları (TEST/doğrulama için): merkez=baslangic+(i,j),
 * cw=true → saat yönü (azalan açı), false → ters. Tam çember start≈end ise ±2π.
 * start hariç, end dahil noktalar.
 */
export function gkodYayTessela(baslangic: IXY, bitis: IXY, i: number, j: number, cw: boolean, adim = Math.PI / 18): IXY[] {
    const cx = baslangic.x + i, cy = baslangic.y + j;
    const r = Math.hypot(i, j);
    const a0 = Math.atan2(baslangic.y - cy, baslangic.x - cx);
    const a1 = Math.atan2(bitis.y - cy, bitis.x - cx);
    const tamCember = Math.hypot(bitis.x - baslangic.x, bitis.y - baslangic.y) < 1e-9;
    let sweep: number;
    if (tamCember) {
        sweep = cw ? -2 * Math.PI : 2 * Math.PI;
    } else if (cw) {
        sweep = a1 - a0; while (sweep > 1e-12) sweep -= 2 * Math.PI;   // negatif (saat yönü)
    } else {
        sweep = a1 - a0; while (sweep < -1e-12) sweep += 2 * Math.PI;  // pozitif
    }
    const n = Math.max(2, Math.ceil(Math.abs(sweep) / adim));
    const out: IXY[] = [];
    for (let k = 1; k <= n; k++) { const a = a0 + sweep * (k / n); out.push({ x: cx + r * Math.cos(a), y: cy + r * Math.sin(a) }); }
    return out;
}

/** Bir halkada hedefe en yakın noktanın index'i */
export function enYakinNoktaIndex(halka: IXY[], hedef: IXY): number {
    let best = 0, bestD = Infinity;
    for (let i = 0; i < halka.length; i++) {
        const dx = halka[i].x - hedef.x, dy = halka[i].y - hedef.y;
        const d = dx * dx + dy * dy;
        if (d < bestD) { bestD = d; best = i; }
    }
    return best;
}

/**
 * Offset halkalarını (dıştan içe) tek sürekli spirale bağlar.
 * Her halkaya, öncekinin son noktasına en yakın noktadan başlanır.
 */
export function halkalariSpiraleBagla(halkalar: IXY[][]): IXY[] {
    const spiral: IXY[] = [];
    for (let h = 0; h < halkalar.length; h++) {
        const halka = halkalar[h];
        if (halka.length < 2) continue;
        let baslangicIdx = 0;
        if (spiral.length > 0) baslangicIdx = enYakinNoktaIndex(halka, spiral[spiral.length - 1]);
        const m = halka.length;
        for (let k = 0; k <= m; k++) {
            const p = halka[(baslangicIdx + k) % m];
            spiral.push({ x: p.x, y: p.y });
        }
    }
    return spiral;
}
